/**
 * iPad portrait — `docs/new-request.md` §16.2, Board 13 B.
 *
 * Under 1,180 px there is no room for the schedule beside the form, so it
 * takes the 620 panel instead: *‹ Back to the request*, the person and *One ·
 * Side by side* along the top, Best times stacked, the strip, the day beside
 * its map, and a footer that says the time and puts it on the form —
 * **Use this time**. Until then a pick is only this screen's; *Back* leaves
 * the form as it was.
 */
import { useRef } from 'react'
import { timeOf, useRequestSheet } from '../sheet-context.js'
import { OnePane, SidePane, UseThisTime, timeWords, useEscapeCloses } from './panes.js'
import { useChoice } from './use-schedule.js'

export function NarrowSchedule() {
  const api = useRequestSheet()
  const root = useRef<HTMLDivElement>(null)
  const choice = useChoice('pending')
  useEscapeCloses(root, api.closeSchedule, Boolean(api.schedule))

  const schedule = api.schedule
  if (!schedule || schedule.people.length === 0) return null
  const minutes = api.facts.minutes
  const shown = choice.shown

  return (
    <div ref={root} className="flex min-h-0 flex-1 flex-col" data-schedule-narrow={schedule.mode}>
      {schedule.mode === 'one'
        ? (
          <OnePane
            variant="narrow"
            choice={choice}
            footer={(view, added) => {
              const person = schedule.people[0]!
              const mine = shown && shown.personId === person.id ? shown : null
              const best = view?.best[0] ?? null
              const words = mine
                ? timeWords(mine.date, mine.start, minutes, view && view.day.date === mine.date ? added : null)
                : best ? timeWords(best.date, best.start, minutes, best.added) : 'No time picked yet'
              return (
                <UseThisTime
                  words={words}
                  onUse={() => {
                    if (choice.pending) choice.commit()
                    else if (!mine && best) api.choose(person, timeOf(best))
                    api.closeSchedule()
                  }}
                />
              )
            }}
          />
        )
        : (
          <SidePane
            variant="narrow"
            choice={choice}
            footer={(
              <UseThisTime
                words={shown ? timeWords(shown.date, shown.start, minutes, null) : 'Click a green gap to book'}
                onUse={() => { choice.commit(); api.closeSchedule() }}
              />
            )}
          />
        )}
    </div>
  )
}
